<template>
  <div class="h-dvh flex flex-col bg-pitch-black text-total-white">
    <TopNavbar
      :isThread="true"
      :userRole="userRole"
      :projectId="project?.id"
      :isSimulation="project?.is_simulation"
      :projectName="project?.address_1"
    />
    <div v-if="isResolving" class="flex justify-center items-center h-screen">
      <div class="text-center">
        <div
          class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-prime-golden border-t-transparent"
          role="status"
        ></div>
        <p class="mt-2 text-total-white">Processing email addresses...</p>
      </div>
    </div>

    <!-- Header Section -->
    <div class="flex justify-between px-4 p-4 bg-eerie-black items-center">
      <a
        @click="goBack"
        class="text-prime-golden text-lg font-medium font-avenir flex items-center"
      >
        <img
          class="w-6 h-6 inline mr-2"
          src="@/assets/images/left-chevron.svg"
          alt="Back"
        />
      </a>

      <!-- Header Actions -->
      <div class="flex space-x-6">
        <button v-if="canReply" @click="toggleReplyDrawer" class="text-prime-golden">
          <img src="../../assets/images/reply-icon.svg" class="w-[18px]" />
        </button>
        <button
          @click="
            showDeleteModal = true;
            prepareEmailThreadForDeletion();
          "
          class="text-prime-golden"
        >
          <img src="../../assets/images/trash-icon.svg" class="w-[18px]" />
        </button>

        <!--<button @click="editEmail" class="text-prime-golden">
          <img src="../../assets/images/edit.svg" class="fa-edit w-7 -mb-1" />
        </button>-->
      </div>
    </div>

    <!-- Threaded Email Logs Section -->
    <div class="overflow-y-auto flex flex-col">
      <div
        v-for="log in threadMessages.sort((a, b) => new Date(b.time) - new Date(a.time))"
        :key="log.id"
        class="bg-eerie-black border-b border-dark-grey pt-4"
      >
        <!-- Email Header -->
        <div class="flex justify-between items-start px-4">
          <div class="flex flex-col items-start max-w-[70vw]">
            <h2 class="flex flex-wrap font-semibold">
              <span
                v-for="email in log.sender.split(',')"
                :key="email"
                @click="openContactPage(email.trim())"
              >
                {{ getStakeHolderNameByEmail(email.trim())
                }}{{
                  email !== log.sender.split(",")[log.sender.split(",").length - 1]
                    ? ", "
                    : ""
                }}
              </span>
            </h2>
            <div class="text-sm flex flex-wrap text-total-white-700">
              <span class="text-total-white mr-1">To:</span>
              <div
                v-for="(email, index) in log.to.split(',')"
                :key="email"
                @click="openContactPage(email.trim())"
              >
                {{ getStakeHolderNameByEmail(email.trim())
                }}<span class="mr-[2px]" v-if="index !== log.to.split(',').length - 1"
                  >,</span
                >
              </div>
            </div>
            <div class="text-sm flex flex-wrap text-total-white-700">
              <span class="text-total-white mr-1">CC:</span>
              <span
                v-for="email in log.cc.split(',')"
                :key="email"
                @click="openContactPage(email.trim())"
              >
                {{ getStakeHolderNameByEmail(email.trim())
                }}{{ email !== log.cc.split(",")[log.cc.split(",").length - 1] ? "," : ""
                }}<span v-if="email !== log.cc.split(',')[log.cc.split(',').length - 1]"
                  >&nbsp;</span
                >
              </span>
            </div>
            <!-- Handle BCC later -->
            <div v-if="false" class="text-sm flex flex-wrap text-total-white-700">
              <span class="text-total-white mr-1">BCC:</span>
              <span
                v-for="email in log.bcc.split(',')"
                :key="email"
                @click="openContactPage(email.trim())"
              >
                {{ getStakeHolderNameByEmail(email.trim())
                }}{{
                  email !== log.bcc.split(",")[log.bcc.split(",").length - 1] ? "," : ""
                }}<span v-if="email !== log.bcc.split(',')[log.bcc.split(',').length - 1]"
                  >&nbsp;</span
                >
              </span>
            </div>
          </div>
          <div class="flex flex-col items-end gap-4">
            <p class="text-sm whitespace-nowrap text-right text-total-white">
              {{ formatDateTime(log.time) }}
            </p>
            <button
              @click="
                showDeleteModal = true;
                prepareEmailForDeletion(log.id);
              "
              class="text-prime-golden"
            >
              <img src="../../assets/images/trash-icon.svg" class="w-[18px]" />
            </button>
          </div>
        </div>

        <!-- Project and Metadata -->
        <div class="flex items-center text-sm px-4 py-2">
          <span>
            Project: <bold class="font-semibold">{{ log.project }}</bold>
          </span>
        </div>

        <!-- Email Subject -->
        <h3 class="font-semibold text-left px-4 text-xl py-2">
          {{ log.subject }}
        </h3>

        <!-- Email Body -->
        <div class="px-4 py-4 pt-6 mt-2 bg-eerie-black pb-10">
          <TiptapEditor 
            :content="log.body" 
            :editable="false"
          />
        </div>

        <!-- File Attachments -->
        <div
          v-if="log.files?.length > 0"
          class="flex flex-col gap-4 px-4 py-2 bg-eerie-black pb-4"
        >
          <span class="text-sm text-total-white text-left">Attachments:</span>
          <div
            v-for="file in log.files"
            :key="file.file_name"
            class="text-sm text-total-white"
          >
            <div
              v-if="file.file_type?.startsWith('image/')"
              class="flex flex-col items-start gap-2 bg-eerie-black rounded-lg p-2"
            >
              <p class="text-sm text-total-white text-left break-all">{{ file.file_name }}</p>
              <template v-if="file.file_type?.startsWith('image/')">
                <img
                  v-if="file.file_type !== 'image/heic'"
                  :src="file.file_url"
                  alt="Attachment Image"
                  @click="openImageModal(file.file_url)"
                  class="cursor-pointer"
                />
                <video
                  v-else
                  :src="file.file_url"
                  autoplay
                  muted
                  loop
                  class="cursor-pointer max-w-[100vw] max-h-[100vh] object-contain z-2"
                  @click="openImageModal(file.file_url)"
                ></video>
              </template>
            </div>
            <div
              v-else
              class="flex items-center gap-2 bg-eerie-black rounded-lg p-2 inline-block max-w-full"
            >
              <div class="flex flex-col items-start">
                <span class="break-all">{{ file.file_name }}</span>
                <span>{{ formatFileSize(file.file_size) }}</span>
              </div>
              <a :href="file.file_url" download class="text-eerie-black">
                <img src="../../assets/images/download.svg" class="w-[18px]" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reply Drawer -->
    <ReplyDrawer
      :isOpen="drawerOpen"
      :emailData="currentEmail"
      :userEmail="userEmail"
      :projectId="selectedProjectId"
      @close="toggleReplyDrawer"
      @openEmailForm="openEmailForm"
    />

    <!-- Action Drawer -->
    <!--<ActionDrawer
      :isOpen="isActionDrawerOpen"
      :emailLog="selectedEmailLog"
      @close="toggleActionDrawer"
      @deleteThread="deleteEmailThread"
      @deleteEmailLog="deleteEmailLog"
    />-->

    <DeleteModal
      :isOpen="showDeleteModal"
      :heading="deletionHeading"
      :message="deletionMessage"
      @confirmDelete="deleteEmailOrEmailThread"
      @cancelDelete="showDeleteModal = false"
    />

    <!-- Image Modal -->
    <div
      v-if="showImageModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-lg"
    >
      <div class="relative w-screen h-screen flex items-center justify-center">
        <button
          @click="showImageModal = false"
          class="absolute top-20 right-4 text-white hover:text-gray-300 z-10"
        >
          <i class="fa-solid fa-times text-2xl"></i>
        </button>
        <img
          :src="currentImage"
          alt="Image"
          class="max-w-[100vw] max-h-[100vh] object-contain"
          @wheel="handleZoom"
          :style="{
            transform: `scale(${zoomLevel})`,
            position: 'absolute',
            top: imagePosition.y + 'px',
            left: imagePosition.x + 'px',
          }"
          @touchstart="startDrag"
          @touchmove="dragImage"
          @touchend="endDrag"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import TiptapEditor from "@/components/TiptapEditor.vue";
import ReplyDrawer from "@/components/ReplyDrawer.vue";
//import ActionDrawer from "@/components/ActionDrawer.vue";
import { useLogStore } from "@/stores/log";
import { useProjectStore } from "@/stores/project";
import { useContactsStore } from "@/stores/contact";
import { useToast } from "vue-toastification";
import DeleteModal from "@/components/DeleteModal.vue";
import { useUserStore } from "@/stores/user";

export default {
  components: {
    TopNavbar,
    TiptapEditor,
    ReplyDrawer,
    //ActionDrawer,
    DeleteModal,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const logStore = useLogStore();
    const projectStore = useProjectStore();
    const contactStore = useContactsStore();
    const userStore = useUserStore();
    const toast = useToast();

    const project = computed(() => projectStore.currentProject);
    const userRole = computed(() => userStore.profile?.account_type);

    // State variables
    const drawerOpen = ref(false);
    const isActionDrawerOpen = ref(false);
    const userEmail = computed(() => userStore.profile?.authorized_email || "");
    const currentEmail = ref(null);
    const selectedEmailLog = ref(null);
    const email = ref(null);

    const showDeleteModal = ref(false);
    const deletionHeading = ref("Delete Email Thread");
    const deletionMessage = ref(
      "Are you sure you want to delete this email thread? This operation might be irreversible."
    );
    const deletionType = ref(null);
    const emailIdToDelete = ref(null);

    const selectedProjectId = ref(route.params?.id || null);

    const showImageModal = ref(false);
    const currentImage = ref(null);
    const zoomLevel = ref(1);
    const imagePosition = ref({ x: 0, y: 0 });
    const isDragging = ref(false);
    const startPosition = ref({ x: 0, y: 0 });

    const isResolving = ref(true);

    // Computed property for thread messages
    const threadMessages = computed(() => {
      return logStore.threadMessages.map((log) => {
        const projectId = log.project_id;
        const project = projectStore.projects.find((p) => p.id === projectId);
        const participants = log.participants || [];

        const getParticipantInfo = (email) => {
          const participant = participants.find((p) => p.email === email);
          return participant
            ? `${participant.first_name} ${participant.last_name} (${participant.email})`
            : email;
        };

        const sender = getParticipantInfo(log.from_contact);
        const to = Array.isArray(log.to_contact)
          ? log.to_contact.map(getParticipantInfo).join(", ")
          : getParticipantInfo(log.to_contact);
        const cc = Array.isArray(log.cc_contact)
          ? log.cc_contact.map(getParticipantInfo).join(", ")
          : "";

        // Process email body to replace image placeholders with actual images
        let processedBody = log.content || "No Content";
        
        // Check for image placeholders and replace with actual images from attachments
        if (log.files && log.files.length > 0) {
          const imageFiles = log.files.filter(file => file.file_type?.startsWith('image/'));
          
          if (imageFiles.length > 0) {
            console.log('Processing email body for images:', processedBody);
            console.log('Found image files:', imageFiles);
          }
          
          // Replace different types of image placeholders with actual img tags
          imageFiles.forEach((file, index) => {
            // Common placeholder patterns
            const placeholders = [
              '[image: inline_image]',
              '[image:inline_image]',
              '[image: ' + file.file_name + ']',
              '[image:' + file.file_name + ']',
              `[image: ${file.file_name}]`,
              `[image:${file.file_name}]`
            ];
            
            placeholders.forEach(placeholder => {
              if (processedBody.includes(placeholder)) {
                const imgTag = `<img src="${file.file_url}" alt="${file.file_name}" class="inline-image max-w-full h-auto rounded my-2" style="display: inline-block;" />`;
                processedBody = processedBody.replace(placeholder, imgTag);
              }
            });
          });
          
          // Also handle generic image placeholders by replacing them with images in order
          let imageIndex = 0;
          processedBody = processedBody.replace(/\[image:\s*inline_image\]/g, () => {
            if (imageIndex < imageFiles.length) {
              const file = imageFiles[imageIndex++];
              return `<img src="${file.file_url}" alt="${file.file_name}" class="inline-image max-w-full h-auto rounded my-2" style="display: inline-block;" />`;
            }
            return '[image: inline_image]'; // Keep original if no more images
          });
        }

        email.value = {
          id: log.id,
          threadId: log.thread_id,
          sender: sender,
          to: to,
          cc: cc,
          subject: log.subject || "No Subject",
          body: processedBody,
          time: log.date_time_created,
          project: project ? project.address_1 : "Loading...",
          files: log.files || [],
        };

        return email.value;
      });
    });

    const fetchThreadMessages = async () => {
      const threadId = route.query.thread_id;
      try {
        await logStore.fetchMessagesByThreadId(threadId, "asc");

        const projectIds = [
          ...new Set(logStore.threadMessages.map((log) => log.project_id)),
        ];
        for (const id of projectIds) {
          if (!projectStore.projects.find((p) => p.id === id)) {
            await projectStore.fetchProjectById(id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch thread messages:", error);
      }
    };

    onMounted(async () => {
      await fetchThreadMessages();
      await contactStore.fetchContactsByProjectId(selectedProjectId.value);
      isResolving.value = false;
    });

    const goBack = () => {
      if (route.query.from_contact) {
        // If the email was initiated from a contact page, go back there.
        router.push({
          name: "ContactViewPage",
          params: { id: route.params.contactId },
          query: { projectId: route.query.projectId },
        });
      } else if (route.params.fromFiles) {
        router.push({
          name: "ProjectPage",
          query: { tab: "files" },
        });
      } else if (route.query.tab) {
        router.push({
          name: "ProjectPage",
          params: { id: route.query.projectId || selectedProjectId.value },
          query: { tab: route.query.tab, readLogs: route.query.readLogs },
        });
      } else {
        // Fallback: simply go back in history.
        router.go(-1);
      }
    };

    const openContactPage = (email) => {
      const participant = contactStore.contacts.find((c) => c.email === email);

      if (participant) {
        router.push({
          name: "ContactViewPage",
          params: { id: participant.id },
          query: {
            from_email: true,
            threadId: route.query.threadId,
            projectId: selectedProjectId.value,
          },
        });
      }
    };

    const canReply = computed(() => {
      if (threadMessages.value.length === 0) return false;
      const firstLog = threadMessages.value[0];
      const contactsList = [];
      if (firstLog.sender) {
        contactsList.push(
          ...firstLog.sender.split(",").map((e) => e.trim().toLowerCase())
        );
      }
      if (firstLog.to) {
        contactsList.push(...firstLog.to.split(",").map((e) => e?.trim()?.toLowerCase()));
      }
      if (firstLog.cc && firstLog.cc !== "") {
        contactsList.push(...firstLog.cc.split(",").map((e) => e?.trim()?.toLowerCase()));
      }
      // Check if any contact in the list belongs to a stakeholder with role "Project Manager"
      return contactsList.some((contact) => {
        const stakeholder = contactStore.contacts.find(
          (s) => s.email?.toLowerCase() === contact || s.phone === contact
        );
        return stakeholder && stakeholder.role === "Project Manager";
      });
    });

    const deleteEmailOrEmailThread = async () => {
      if (deletionType.value === "email") {
        try {
          if (emailIdToDelete.value) {
            await logStore.deleteEmailLog(emailIdToDelete.value);
            toast.success("Email log deleted successfully!");
            router.go(0);
          }
        } catch (error) {
          console.error("Error deleting email log:", error);
          toast.error("Failed to delete email log. Please try again.");
        }
      } else {
        const threadId = route.query.thread_id;
        try {
          if (threadId) {
            await logStore.deleteEmailThread(threadId);
            toast.success("Thread deleted successfully!");
          }

          goBack();
        } catch (error) {
          console.error("Error deleting thread:", error);
          toast.error("Failed to delete thread. Please try again.");
        } finally {
          toggleActionDrawer();
        }
      }
    };

    const prepareEmailForDeletion = (emailId) => {
      emailIdToDelete.value = Number(emailId);
      deletionType.value = "email";
      deletionHeading.value = "Delete Email";
      deletionMessage.value =
        "Are you sure you want to delete this email? This operation might be irreversible.";
    };

    const prepareEmailThreadForDeletion = () => {
      deletionType.value = "thread";
      deletionHeading.value = "Delete Email Thread";
      deletionMessage.value =
        "Are you sure you want to delete this email thread? This operation might be irreversible.";
    };

    const toggleReplyDrawer = () => {
      //currentEmail.value = email;
      currentEmail.value = email.value;
      drawerOpen.value = !drawerOpen.value;
    };

    const toggleActionDrawer = (log) => {
      selectedEmailLog.value = log;
      isActionDrawerOpen.value = !isActionDrawerOpen.value;
    };

    const editEmail = () => {
      console.log("Edit email initiated");
    };

    const formatDateTime = (dateTimeString) => {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        weekday: "short",
        hour: "numeric",
        minute: "numeric",
      };
      return new Date(dateTimeString).toLocaleString(undefined, options);
    };

    const formatFileSize = (size) => {
      return (size / (1024 * 1024)).toFixed(2) + " MB";
    };

    const openEmailForm = (emailData) => {
      router.push({
        name: "NewEmail",
        query: {
          ...emailData,
          projectId: selectedProjectId.value,
          thread_id: route.query.thread_id,
          from_contact: route.query.from_contact || "",
          currentTab: route.query.currentTab || "log",
        },
      });
    };

    const getStakeHolderNameByEmail = (email) => {
      if (!email || email?.trim()?.toLowerCase() === "") return "";
      const stakeholder = contactStore.contacts.find((s) => s.email == email);
      if (stakeholder?.first_name == "" && stakeholder?.last_name == "") {
        return email;
      }
      return stakeholder ? `${stakeholder.first_name}${stakeholder.last_name ? ' ' + stakeholder.last_name : ''}` : email;
    };

    const openImageModal = (imageUrl) => {
      currentImage.value = imageUrl;
      showImageModal.value = true;
      // Reset position to center the image
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const imgWidth = img.width;
        const imgHeight = img.height;
        const heightFactor = imgHeight / imgWidth;
        const widthFactor = imgWidth / windowWidth;

        const effectiveHeight = widthFactor * heightFactor;

        imagePosition.value = {
          x: 0,
          y: (windowHeight - effectiveHeight) / 4,
        };
      };
    };

    const handleZoom = (event) => {
      event.preventDefault();
      if (event.deltaY < 0) {
        zoomLevel.value = Math.min(zoomLevel.value + 0.1, 3);
      } else {
        zoomLevel.value = Math.max(zoomLevel.value - 0.1, 0.5);
      }
    };

    const startDrag = (event) => {
      isDragging.value = true;
      startPosition.value = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    };

    const dragImage = (event) => {
      if (isDragging.value) {
        const dx = event.touches[0].clientX - startPosition.value.x;
        const dy = event.touches[0].clientY - startPosition.value.y;
        imagePosition.value.x += dx;
        imagePosition.value.y += dy;
        startPosition.value = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
      }
    };

    const endDrag = () => {
      isDragging.value = false;
    };

    watch(showImageModal, (newValue) => {
      if (!newValue) {
        zoomLevel.value = 1; // Reset zoom level when closing modal
      }
    });

    return {
      drawerOpen,
      isActionDrawerOpen,
      userEmail,
      currentEmail,
      threadMessages,
      goBack,
      toggleReplyDrawer,
      toggleActionDrawer,
      editEmail,
      formatDateTime,
      openEmailForm,
      selectedProjectId,
      showDeleteModal,
      deletionHeading,
      deletionMessage,
      deletionType,
      prepareEmailForDeletion,
      prepareEmailThreadForDeletion,
      deleteEmailOrEmailThread,
      getStakeHolderNameByEmail,
      formatFileSize,
      canReply,
      showImageModal,
      currentImage,
      zoomLevel,
      openImageModal,
      handleZoom,
      imagePosition,
      startDrag,
      dragImage,
      endDrag,
      openContactPage,
      project,
      userRole,
    };
  },
};
</script>

<style scoped>
/* Custom styles if needed */
</style>
